// contexts/LangueInterfaceContext.jsx
// Détermine la langue d'interface active et l'applique à i18next.
// Priorité : choix manuel fait dans cette session > préférence du profil (si connecté)
// > paramètre WP `langueInterface` > repli 'fr'.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n/config';
import { useAuth } from './AuthContext';
import { useLangueInterfaceParam } from '../hooks/useLangueInterfaceParam';

const LANGUES_VALIDES = ['fr', 'es', 'en'];

// Domaine WP à cibler pour le signal de changement de langue (voir contrat validé avec le hub/dev WP)
const WP_ORIGIN = 'https://vocaverb.com';

const LangueInterfaceContext = createContext(null);

export function LangueInterfaceProvider({ children }) {
  const { user, updateProfil } = useAuth();
  const langueInterfaceParam = useLangueInterfaceParam();
  // Choix manuel fait dans l'app (prioritaire, y compris pour un visiteur non connecté)
  const [overrideLangue, setOverrideLangue] = useState(null);

  const langueInterface = useMemo(() => {
    if (overrideLangue) return overrideLangue;
    if (user?.langue && LANGUES_VALIDES.includes(user.langue)) return user.langue;
    return langueInterfaceParam || 'fr';
  }, [overrideLangue, user, langueInterfaceParam]);

  useEffect(() => {
    i18n.changeLanguage(langueInterface);
  }, [langueInterface]);

  // Changement manuel de langue (sélecteur menu ou page profil)
  const setLangueInterface = async (nouvelleLangue) => {
    if (!LANGUES_VALIDES.includes(nouvelleLangue)) return;

    setOverrideLangue(nouvelleLangue);

    if (user) {
      try {
        await updateProfil({ langue: nouvelleLangue });
      } catch (err) {
        // La préférence reste appliquée localement même si la sauvegarde échoue
      }
    }

    // Signal vers WP, uniquement en mode embed (iframe) — le listener côté WP reste à construire séparément
    const isEmbedNow = new URLSearchParams(window.location.search).get('embed') === 'true';
    if (isEmbedNow && window.parent !== window) {
      window.parent.postMessage({ type: 'vv:langue-interface-changed', langue: nouvelleLangue }, WP_ORIGIN);
    }
  };

  const value = { langueInterface, setLangueInterface };

  return <LangueInterfaceContext.Provider value={value}>{children}</LangueInterfaceContext.Provider>;
}

export function useLangueInterface() {
  const context = useContext(LangueInterfaceContext);
  if (!context) {
    throw new Error('useLangueInterface doit être utilisé dans un LangueInterfaceProvider');
  }
  return context;
}

export default LangueInterfaceContext;
